import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Home, Briefcase, Newspaper, Users, Mail } from 'lucide-react';
import { Button } from './ui/button';
import logo from './assets/logo.png';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const services = [
    { name: 'Web Development', path: '/services/web-development' },
    { name: 'UI/UX Design', path: '/services/ui-ux-design' },
    { name: 'Mobile Apps', path: '/services/mobile-apps' },
    { name: 'AI Integration', path: '/services/ai-integration' },
    { name: 'Cybersecurity', path: '/services/cybersecurity' },
    { name: 'Cloud Solutions', path: '/services/cloud-solutions' },
  ];

  const technologies = [
    { name: 'Frontend Development', path: '/technologies/frontend' },
    { name: 'Backend Development', path: '/technologies/backend' },
    { name: 'Database Systems', path: '/technologies/databases' },
    { name: 'DevOps & Cloud', path: '/technologies/devops' },
  ];

  const industries = [
    { name: 'Finance & Banking', path: '/industries/finance' },
    { name: 'Healthcare', path: '/industries/healthcare' },
    { name: 'E-commerce & Retail', path: '/industries/ecommerce' },
    { name: 'Education & E-Learning', path: '/industries/education' },
  ];

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? 'bg-[rgba(10,37,64,0.98)] py-2 shadow-[0_0_30px_rgba(0,168,255,0.5)]' 
            : 'bg-[rgba(10,37,64,0.95)] py-3 shadow-[0_0_30px_rgba(0,168,255,0.5)]'
        } border-b-2 border-[#00a8ff] backdrop-blur-md`}
      >
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3 group">
              <img
                src={logo}
                alt="Max Bien Logo"
                className="h-12 transition-all duration-300 group-hover:scale-110"
                style={{ filter: 'brightness(0) saturate(100%) invert(61%) sepia(98%) saturate(3180%) hue-rotate(175deg) brightness(103%) contrast(101%) drop-shadow(0 0 15px #00a8ff)' }}
              />
              <span className="text-2xl font-bold led-text tracking-wider animate-led-pulse hidden sm:block">
                MAX BIEN
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1">
              <Link
                to="/"
                className={`flex items-center space-x-2 px-4 py-2 rounded-md font-bold transition-all duration-300 hover:bg-[rgba(0,168,255,0.2)] led-text hover:shadow-[0_0_20px_rgba(0,168,255,0.8)] ${
                  location.pathname === '/' ? 'bg-[rgba(0,168,255,0.3)] shadow-[0_0_20px_rgba(0,168,255,0.6)]' : ''
                }`}
                style={{ textShadow: '0 0 10px #00a8ff' }}
              >
                <Home size={18} />
                <span>Home</span>
              </Link>

              {/* Services Dropdown */}
              <div className="relative group">
                <button className="flex items-center space-x-2 px-4 py-2 rounded-md font-bold transition-all duration-300 hover:bg-[rgba(0,168,255,0.2)] led-text hover:shadow-[0_0_20px_rgba(0,168,255,0.8)]"
                  style={{ textShadow: '0 0 10px #00a8ff' }}>
                  <span>Services</span>
                  <ChevronDown size={16} className="transition-transform duration-300 group-hover:rotate-180" />
                </button>
                {/* Invisible bridge to prevent hover gap */}
                <div className="absolute top-full left-0 w-64 h-2 opacity-0 group-hover:opacity-0" style={{ pointerEvents: 'auto' }}></div>
                <div className="absolute top-full left-0 pt-2 w-64 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50">
                  <div className="bg-[rgba(10,37,64,0.98)] border-2 border-[#00a8ff] rounded-xl shadow-[0_0_30px_#00a8ff] backdrop-blur-md">
                    {services.map((service, idx) => (
                      <Link
                        key={idx}
                        to={service.path}
                        className="flex items-center px-6 py-4 led-text hover:bg-[rgba(0,168,255,0.3)] hover:pl-8 transition-all duration-300 border-b border-[rgba(0,168,255,0.2)] last:border-b-0 first:rounded-t-xl last:rounded-b-xl"
                        style={{ textShadow: '0 0 10px #00a8ff' }}
                      >
                        <span className="text-[#00a8ff] mr-3 text-xl">→</span>
                        <span>{service.name}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              {/* Technologies Dropdown */}
              <div className="relative group">
                <button className="flex items-center space-x-2 px-4 py-2 rounded-md font-bold transition-all duration-300 hover:bg-[rgba(0,168,255,0.2)] led-text hover:shadow-[0_0_20px_rgba(0,168,255,0.8)]"
                  style={{ textShadow: '0 0 10px #00a8ff' }}>
                  <span>Technologies</span>
                  <ChevronDown size={16} className="transition-transform duration-300 group-hover:rotate-180" />
                </button>
                {/* Invisible bridge to prevent hover gap */}
                <div className="absolute top-full left-0 w-64 h-2 opacity-0 group-hover:opacity-0" style={{ pointerEvents: 'auto' }}></div>
                <div className="absolute top-full left-0 pt-2 w-64 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50">
                  <div className="bg-[rgba(10,37,64,0.98)] border-2 border-[#00a8ff] rounded-xl shadow-[0_0_30px_#00a8ff] backdrop-blur-md">
                    {technologies.map((tech, idx) => (
                      <Link
                        key={idx}
                        to={tech.path}
                        className="flex items-center px-6 py-4 led-text hover:bg-[rgba(0,168,255,0.3)] hover:pl-8 transition-all duration-300 border-b border-[rgba(0,168,255,0.2)] last:border-b-0 first:rounded-t-xl last:rounded-b-xl"
                        style={{ textShadow: '0 0 10px #00a8ff' }}
                      >
                        <span className="text-[#00a8ff] mr-3 text-xl">→</span>
                        <span>{tech.name}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              {/* Industries Dropdown */}
              <div className="relative group">
                <button className="flex items-center space-x-2 px-4 py-2 rounded-md font-bold transition-all duration-300 hover:bg-[rgba(0,168,255,0.2)] led-text hover:shadow-[0_0_20px_rgba(0,168,255,0.8)]"
                  style={{ textShadow: '0 0 10px #00a8ff' }}>
                  <span>Industries</span>
                  <ChevronDown size={16} className="transition-transform duration-300 group-hover:rotate-180" />
                </button>
                {/* Invisible bridge to prevent hover gap */}
                <div className="absolute top-full left-0 w-64 h-2 opacity-0 group-hover:opacity-0" style={{ pointerEvents: 'auto' }}></div>
                <div className="absolute top-full left-0 pt-2 w-64 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50">
                  <div className="bg-[rgba(10,37,64,0.98)] border-2 border-[#00a8ff] rounded-xl shadow-[0_0_30px_#00a8ff] backdrop-blur-md">
                    {industries.map((industry, idx) => (
                      <Link
                        key={idx}
                        to={industry.path}
                        className="flex items-center px-6 py-4 led-text hover:bg-[rgba(0,168,255,0.3)] hover:pl-8 transition-all duration-300 border-b border-[rgba(0,168,255,0.2)] last:border-b-0 first:rounded-t-xl last:rounded-b-xl"
                        style={{ textShadow: '0 0 10px #00a8ff' }}
                      >
                        <span className="text-[#00a8ff] mr-3 text-xl">→</span>
                        <span>{industry.name}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              <Link
                to="/portfolio"
                className={`flex items-center space-x-2 px-4 py-2 rounded-md font-bold transition-all duration-300 hover:bg-[rgba(0,168,255,0.2)] led-text hover:shadow-[0_0_20px_rgba(0,168,255,0.8)] ${
                  location.pathname === '/portfolio' ? 'bg-[rgba(0,168,255,0.3)] shadow-[0_0_20px_rgba(0,168,255,0.6)]' : ''
                }`}
                style={{ textShadow: '0 0 10px #00a8ff' }}
              >
                <Briefcase size={18} />
                <span>Portfolio</span>
              </Link>

              <Link
                to="/blog"
                className={`flex items-center space-x-2 px-4 py-2 rounded-md font-bold transition-all duration-300 hover:bg-[rgba(0,168,255,0.2)] led-text hover:shadow-[0_0_20px_rgba(0,168,255,0.8)] ${
                  location.pathname === '/blog' ? 'bg-[rgba(0,168,255,0.3)] shadow-[0_0_20px_rgba(0,168,255,0.6)]' : ''
                }`}
                style={{ textShadow: '0 0 10px #00a8ff' }}
              >
                <Newspaper size={18} />
                <span>Blog</span>
              </Link>

              <Link
                to="/team"
                className={`flex items-center space-x-2 px-4 py-2 rounded-md font-bold transition-all duration-300 hover:bg-[rgba(0,168,255,0.2)] led-text hover:shadow-[0_0_20px_rgba(0,168,255,0.8)] ${
                  location.pathname === '/team' ? 'bg-[rgba(0,168,255,0.3)] shadow-[0_0_20px_rgba(0,168,255,0.6)]' : ''
                }`}
                style={{ textShadow: '0 0 10px #00a8ff' }}
              >
                <Users size={18} />
                <span>Team</span>
              </Link>

              <Link to="/contact">
                <Button
                  className="flex items-center space-x-2 bg-transparent border-2 border-[#00a8ff] text-[#00a8ff] hover:bg-[#00a8ff] hover:text-[#0a2540] font-bold transition-all duration-300 hover:scale-110"
                  style={{ 
                    boxShadow: '0 0 20px rgba(0,168,255,0.5)',
                    textShadow: '0 0 10px #00a8ff'
                  }}
                >
                  <Mail size={18} />
                  <span>Contact</span>
                </Button>
              </Link>
            </nav>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden text-[#00a8ff] p-2 led-text hover:scale-110 transition-transform"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              style={{ textShadow: '0 0 15px #00a8ff' }}
            >
              {isMobileMenuOpen ? <X size={32} /> : <Menu size={32} />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMobileMenuOpen && (
            <nav className="lg:hidden mt-4 pb-4 space-y-2 border-t-2 border-[#00a8ff] pt-4 animate-fade-in">
              <Link
                to="/"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-4 py-3 led-text font-bold hover:bg-[rgba(0,168,255,0.2)] rounded-md transition-all"
                style={{ textShadow: '0 0 10px #00a8ff' }}
              >
                Home
              </Link>
              {services.map((service, idx) => (
                <Link
                  key={idx}
                  to={service.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block px-4 py-3 led-text font-bold hover:bg-[rgba(0,168,255,0.2)] rounded-md transition-all pl-8"
                  style={{ textShadow: '0 0 10px #00a8ff' }}
                >
                  → {service.name}
                </Link>
              ))}
              <Link
                to="/portfolio"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-4 py-3 led-text font-bold hover:bg-[rgba(0,168,255,0.2)] rounded-md transition-all"
                style={{ textShadow: '0 0 10px #00a8ff' }}
              >
                Portfolio
              </Link>
              <Link
                to="/blog"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-4 py-3 led-text font-bold hover:bg-[rgba(0,168,255,0.2)] rounded-md transition-all"
                style={{ textShadow: '0 0 10px #00a8ff' }}
              >
                Blog
              </Link>
              <Link
                to="/team"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-4 py-3 led-text font-bold hover:bg-[rgba(0,168,255,0.2)] rounded-md transition-all"
                style={{ textShadow: '0 0 10px #00a8ff' }}
              >
                Team
              </Link>
              <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)}>
                <Button className="w-full bg-transparent border-2 border-[#00a8ff] text-[#00a8ff] hover:bg-[#00a8ff] hover:text-[#0a2540] font-bold" style={{ boxShadow: '0 0 20px #00a8ff' }}>
                  Contact Us
                </Button>
              </Link>
            </nav>
          )}
        </div>
      </header>
    </>
  );
};